import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { determineCalendarFetchParams, formatDayHeaderLabel, generateDayKey } from '../functions/schedulingDateUtils';
import { useModal } from 'components/modals/ModalContext';
import AddJobOrActivityForm from '../AddJobOrActivityForm';
import AddActivityForm from '../AddActivityForm';
import { CalendarDraggingPlaceholder, EventsListType, EventType, SchedulerDraggableId, SchedulerDroppableId, SchedulingView } from '../SchedulingTypes';
import { useFetchData } from 'services/api/useFetchData';
import { generateEvents } from '../functions/generateEvents';
import { useSettings } from 'services/settings/SettingsContext';
import { getOverlappingEvents } from '../functions/calculateOverlappingEvents';
import { Droppable } from 'services/dragdrop/Droppable';
import { DragDropEvent, useDragDropContext } from 'services/dragdrop/DragDropContext';
import useAdjustCalendarSize from './functions/useAdjustCalendarSize';
import { useGlobalContext } from 'GlobalContext';
import CalendarEvent from './CalendarEvent';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import RightClickMenu, { CopiedEvent, RightClickMenuType } from '../RightClickMenu';

interface PersonalCalendarProps {
    view: SchedulingView;
    selectedUser: number
    selectedUserHash: string | null;
    days: Date[];
    timeSlots: string[] | undefined;
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId> | null;
    refreshTrigger: number;
    onRefresh: () => void;
}

const PersonalCalendar: React.FC<PersonalCalendarProps> = ({
    view, selectedUser, selectedUserHash, days, timeSlots, dragResult, refreshTrigger, onRefresh
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { handleLogout } = useAuthContext();
    const { isTopNavbarEnabled } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const { dragging, isDragging, dragOver } = useDragDropContext<SchedulerDraggableId, SchedulerDroppableId>();
    const [events, setEvents] = useState<EventsListType>({});
    const [eventOverlapMap, setEventOverlapMap] = useState(new Map<string, EventType[]>());
    const [draggingPlaceholder, setDraggingPlaceholder] = useState<CalendarDraggingPlaceholder | null>(null);
    const calendarRef = useRef<HTMLDivElement | null>(null);
    const { pixelsPerMinute } = useAdjustCalendarSize(calendarRef, isTopNavbarEnabled, timeSlots);
    const [lastSync, setLastSync] = useState<string | null>(null);
    const [rightClickMenu, setRightClickMenu] = useState<RightClickMenuType | null>(null);
    const [copiedEvent, setCopiedEvent] = useState<CopiedEvent | null>(null);

    // Set the event fetch params
    const calendarFetchParams = useMemo(() => {
        if (!days || days.length === 0 || !selectedUser) return {};
        return determineCalendarFetchParams(days, selectedUser);
    }, [JSON.stringify(days), selectedUser]);

    // Fetch the events
    const { response: fetchedEvents, loading: eventsLoading } = useFetchData({
        apiUrl: Object.keys(calendarFetchParams).length > 0 ? "get_calendar_events" : "",
        params: calendarFetchParams
    });

    // Set the fetched events
    useEffect(() => {
        if (fetchedEvents) {;
            const { generatedEvents, updatedOverlapMap } = generateEvents(
                fetchedEvents, userLocale, userTimezone, true, days, [], {}, new Map()
            );

            setEvents(generatedEvents);
            setEventOverlapMap(updatedOverlapMap);

            // Set the last sync time to start the polling for updates
            setLastSync(fetchedEvents.server_time);
        }
    }, [fetchedEvents]);

    // Poll for updates  
    const fetchDeltaEvents = useCallback(async () => {
        if (Object.keys(calendarFetchParams).length > 0 && lastSync) {

            // Get the last changed events 
            const response = await fetchData({
                apiUrl: 'get_calendar_events',
                params: { ...calendarFetchParams, modified_since: lastSync },
                handleLogout
            });

            if (response) {
                // Regenerate the events
                const { generatedEvents, updatedOverlapMap } = generateEvents(
                    response, userLocale, userTimezone, view?.timeDisplayMode === 'timeslots', days, [], events, eventOverlapMap, true
                );

                // Update the event states
                setEvents(generatedEvents);
                setEventOverlapMap(updatedOverlapMap);

                // Update the last sync time
                setLastSync(response.server_time);
            }
        }
    }, [calendarFetchParams, lastSync, handleLogout, userLocale, userTimezone, view?.timeDisplayMode, days, [], events, eventOverlapMap]);
    
    // Refresh the events every 10 seconds
    useEffect(() => {
        const interval = setInterval(fetchDeltaEvents, 10000);
        return () => clearInterval(interval);
    }, [fetchDeltaEvents]);

    // Refresh the events on trigger, for example after adding a job
    useEffect(() => {
        fetchDeltaEvents();
    }, [refreshTrigger]);

    // Close the right click menu, if opened
    useEffect(() => {
        const handleClick = () => setRightClickMenu(null);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    // Handle the copy of an event
    const handleCopyEvent = (event: EventType) => {
        if (event.type === 'appointment') {
            setCopiedEvent({
                type: 'appointment',
                data: {
                    appointmentId: event.appointmentId,
                    durationMinutes: event.durationMinutes ?? 60,
                    startTime: event.startTime,
                    userId: selectedUser,
                    dayKey: event.dayKey
                },
                lastUsedAt: Date.now()
            });
        }

        if (event.type === 'activity') {
            setCopiedEvent({
                type: 'activity',
                data: {
                    activityId: event.activityId,
                    durationMinutes: event.durationMinutes ?? 60,
                    startTime: event.startTime,
                    userId: selectedUser,
                    dayKey: event.dayKey
                },
                lastUsedAt: Date.now()
            });
        }
    };

    // Handle the click on an empty time slot to add a job or activity for the selected user
    const handleAddJob = (e: React.MouseEvent, dayKey: string, timeSlot?: string) => {
        e.preventDefault();

        // Prefill the form with the selected user, date and time
        const selectedCalendarPosition = {
            selectedResource: selectedUserHash,
            selectedDate: dayKey,
            selectedTime: timeSlot,
        }

        // Open the add job form when creating jobs is enabled, otherwise open the add activity form
        if (view.createJobs) {
            initializeModal(
                React.cloneElement(<AddJobOrActivityForm />, { linkedItem: selectedCalendarPosition }), {
                    title: t('job.general.direct_schedule_label'),
                    modalSize: 'add-form',
                    onSuccess: () => { onRefresh(); }
                }
            );
        } else {
            initializeModal(
                React.cloneElement(<AddActivityForm />, { linkedItem: selectedCalendarPosition }), {
                    title: t('scheduling.other_activity.add_activity_label'),
                    modalSize: 'add-form',
                    onSuccess: () => { onRefresh(); }
                }
            );
        }
    }

    // Handle show right click menu
    const handleShowRightClickMenu = (e: React.MouseEvent, dayKey: string, timeSlot?: string) => {
        e.preventDefault();

        setRightClickMenu({
            type: 'empty',
            x: e.clientX,
            y: e.clientY,
            userId: selectedUser,
            dayKey,
            timeSlot
        });
    };

    // Group the events per day
    const eventsByDay = useMemo(() => {
        const groupedEvents: Record<string, EventType[]> = {};

        Object.values(events).forEach(event => {
            if (!groupedEvents[event.dayKey]) {
                groupedEvents[event.dayKey] = [];
            }

            groupedEvents[event.dayKey].push(event);
        });

        return groupedEvents;
    }, [events]);

    // Get todays date as translated day header label
    const today = formatDayHeaderLabel(new Date());

    // Close the right click menu
    const closeRightClickMenu = () => setRightClickMenu(null);

    // Handle the completion of an event drag
    useEffect(() => {
        if (dragResult) {
            const handleDragResult = async () => {
                // switch (dragResult.draggableId.type) {
                //     // Handle appointment drag
                //     case 'appointment':
                //         handleAppointmentDrag(
                //             dragResult, 
                //             events,
                //             resourceGroups,
                //             userLocale, 
                //             userTimezone,
                //             setFloatingAlert, 
                //             setEvents, 
                //             setEventsByResource
                //         );

                //     default:
                //         return null;
                // }
            }
            handleDragResult();
        }
    }, [dragResult]);

    // Set the dragging placeholder
    useEffect(() => {
        // Reset dragging placeholder
        if (!isDragging || !dragOver) {
            setDraggingPlaceholder(null);
            return;
        }
    
        // Determine the duration minutes
        let durationMinutes = 60;
        if (dragging?.draggableId?.type === 'appointment' || dragging?.draggableId?.type === 'activity') {
            durationMinutes = dragging.draggableId.event.durationMinutes ?? 60;
        }

        // Set the dragging placeholder
        if (dragOver.type === 'timeSlot') {
            const { day, timeSlot } = dragOver;
            setDraggingPlaceholder({ day, timeSlot, durationMinutes });
        } 
    }, [isDragging, dragOver, dragging]);

    const timeSlotHeight = pixelsPerMinute ? (pixelsPerMinute * 30) : '40';

    return (
        <div 
            ref={calendarRef}
            className="personal-calendar"
        >
            {/* Header */}
            <div className={`header grid-${days.length + 1}-columns`}>
                <div className="time-column" />
                {days.map(day => (
                    <div key={day.toISOString()} className="day-header">
                        <span className={formatDayHeaderLabel(day) === today ? 'today' : ''}>
                            {formatDayHeaderLabel(day) === today ? (
                                t('date_time.relative_days.today')
                            ): (
                                formatDayHeaderLabel(day)
                            )}
                        </span>
                    </div>
                ))}
            </div>

            <div className={`calendar-body grid-${days.length + 1}-columns`}>

                {/* TIME COLUMN */}
                <div className="time-column">
                    {timeSlots?.map(timeSlot => (
                        <div key={timeSlot} className={`time-cell ${timeSlot.endsWith(':30') ? 'half-hour' : 'whole-hour'}`}>
                            {timeSlot.endsWith(':00') && timeSlot}
                        </div>
                    ))}
                </div>

                {/* DAYS */}
                {days.map(day => {
                    const dayKey = generateDayKey(day);
                    const dayEvents = eventsByDay[dayKey] ?? [];

                    return (
                        <div key={dayKey} className="day-column">
                            <div className="day-grid">
                                {timeSlots?.map(timeSlot => {
                                    const dragOverCurrentTimeSlot =
                                        draggingPlaceholder?.day === dayKey &&
                                        draggingPlaceholder?.timeSlot === timeSlot;

                                    return (
                                        <Droppable
                                            key={`${dayKey}-${timeSlot}`}
                                            droppableId={{
                                                type: 'timeSlot',
                                                resourceId: selectedUser,
                                                day: dayKey,
                                                timeSlot
                                            }}
                                            showPlaceholder={false}
                                        >
                                            <div
                                                className={`time-slot ${timeSlot.endsWith(':30') ? 'half-hour' : 'whole-hour'}`}
                                                style={{ height: `${timeSlotHeight}px` }}
                                                onClick={(e) => handleAddJob(e, dayKey, timeSlot)}
                                                onContextMenu={(e) => handleShowRightClickMenu(e, dayKey, timeSlot)}
                                            >
                                                {dragOverCurrentTimeSlot && (
                                                    <div className="dragging-placeholder" />
                                                )}
                                            </div>
                                        </Droppable>
                                    );
                                })}
                            </div>

                            <div className="events-layer">
                                {dayEvents.filter(event => !event.deleted).map(event => (
                                    <CalendarEvent
                                        key={event.key}
                                        event={event}
                                        appointmentTextOptions={view.appointmentText}
                                        colorMethod={view.colorMode}
                                        currentResource={selectedUser}
                                        currentDay={dayKey}
                                        pixelsPerMinute={pixelsPerMinute}
                                        dayStartTime={timeSlots?.[0] ?? '00:00'} 
                                        overlappingEvents={
                                            getOverlappingEvents(
                                                eventOverlapMap,
                                                String(selectedUser),
                                                dayKey,
                                                event.timeSlot ?? ''
                                            )
                                        }
                                        onOpenRightClickMenu={setRightClickMenu}
                                        onCloseRightClickMenu={closeRightClickMenu}
                                        onShowTooltip={() => {}}
                                        onHideTooltip={() => {}}
                                        onRefresh={onRefresh}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
                {rightClickMenu && (
                    <RightClickMenu
                        viewType={view.viewType}
                        menu={rightClickMenu}
                        copiedEvent={copiedEvent}
                        userLocale={userLocale}
                        userTimezone={userTimezone}
                        onCopy={handleCopyEvent}
                        onClose={closeRightClickMenu}
                        onRefresh={onRefresh}
                        setCopiedEvent={setCopiedEvent}
                    />
                )}
            </div>
        </div>
    );
};

export default PersonalCalendar;